/**
 * Archivos Adjuntos React Query Hooks
 * TanStack Query hooks for file attachment management
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  activateArchivoAdjunto,
  createArchivoAdjunto,
  createArchivosAdjuntosBatch,
  deactivateArchivoAdjunto,
  deleteArchivoAdjunto,
  getArchivoAdjunto,
  getArchivosAdjuntos,
  type ArchivoAdjuntoCreateData,
  type ArchivoAdjuntoFilters,
  type TipoArchivo,
} from "@/services/archivos-adjuntos";

// Re-export types and constants for pages
export type {
  ArchivoAdjunto,
  ArchivoAdjuntoListItem,
  ArchivoAdjuntoCreateData,
  ArchivoAdjuntoFilters,
  TipoArchivo,
} from "@/services/archivos-adjuntos";
export { TIPOS_ARCHIVO } from "@/services/archivos-adjuntos";

// =============================================================================
// QUERY KEYS
// =============================================================================

export const archivosAdjuntosKeys = {
  all: ["archivos-adjuntos"] as const,
  lists: () => [...archivosAdjuntosKeys.all, "list"] as const,
  list: (filters: ArchivoAdjuntoFilters) => [...archivosAdjuntosKeys.lists(), filters] as const,
  details: () => [...archivosAdjuntosKeys.all, "detail"] as const,
  detail: (id: number) => [...archivosAdjuntosKeys.details(), id] as const,
};

// =============================================================================
// QUERIES
// =============================================================================

/**
 * Hook to fetch paginated archivos adjuntos
 */
export function useArchivosAdjuntos(filters: ArchivoAdjuntoFilters = {}) {
  return useQuery({
    queryKey: archivosAdjuntosKeys.list(filters),
    queryFn: () => getArchivosAdjuntos(filters),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to fetch a single archivo adjunto
 */
export function useArchivoAdjunto(id: number | null) {
  return useQuery({
    queryKey: archivosAdjuntosKeys.detail(id!),
    queryFn: () => getArchivoAdjunto(id!),
    enabled: !!id,
  });
}

// =============================================================================
// MUTATIONS
// =============================================================================

/**
 * Hook to create a new archivo adjunto
 */
export function useCreateArchivoAdjunto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ArchivoAdjuntoCreateData) => createArchivoAdjunto(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: archivosAdjuntosKeys.lists() });
    },
  });
}

/**
 * Hook to create multiple archivos adjuntos (batch upload)
 */
export function useCreateArchivosAdjuntosBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      pacienteId,
      tipo,
      files,
      descripcion,
    }: {
      pacienteId: number;
      tipo: TipoArchivo;
      files: File[];
      descripcion?: string;
    }) => createArchivosAdjuntosBatch(pacienteId, tipo, files, descripcion),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: archivosAdjuntosKeys.lists() });
    },
  });
}

/**
 * Hook to delete an archivo adjunto
 */
export function useDeleteArchivoAdjunto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteArchivoAdjunto(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: archivosAdjuntosKeys.lists() });
    },
  });
}

/**
 * Hook to deactivate an archivo adjunto
 */
export function useDeactivateArchivoAdjunto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deactivateArchivoAdjunto(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: archivosAdjuntosKeys.lists() });
    },
  });
}

/**
 * Hook to activate an archivo adjunto
 */
export function useActivateArchivoAdjunto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => activateArchivoAdjunto(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: archivosAdjuntosKeys.lists() });
    },
  });
}
